import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CompanyTable = ({ 
  companies, 
  sortConfig,
  onSort,
  onViewDetails,
  onEdit,
  onDelete,
  onManageSubscription,
  onManageConfiguration
}) => {
  const getSortIcon = (column) => {
    if (sortConfig?.column !== column) {
      return <Icon name="ChevronsUpDown" size={16} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ChevronUp" size={16} className="text-primary" />
      : <Icon name="ChevronDown" size={16} className="text-primary" />;
  };

  const getSubscriptionBadge = (tier) => {
    const badges = {
      'Starter': 'bg-info/10 text-info border-info/20',
      'Professional': 'bg-primary/10 text-primary border-primary/20',
      'Enterprise': 'bg-warning/10 text-warning border-warning/20'
    };
    return badges?.[tier] || 'bg-muted text-muted-foreground border-border';
  };

  const getDataIsolationBadge = (status) => {
    return status ? 'bg-success/10 text-success border-success/20' : 'bg-error/10 text-error border-error/20';
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th 
                className="px-4 py-3 text-left cursor-pointer hover:bg-muted transition-smooth"
                onClick={() => onSort('businessName')}
              >
                <div className="flex items-center gap-2 font-heading font-semibold text-sm text-foreground">
                  <span>Business Name</span>
                  {getSortIcon('businessName')}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left cursor-pointer hover:bg-muted transition-smooth"
                onClick={() => onSort('subscriptionTier')}
              >
                <div className="flex items-center gap-2 font-heading font-semibold text-sm text-foreground">
                  <span>Subscription</span>
                  {getSortIcon('subscriptionTier')}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left cursor-pointer hover:bg-muted transition-smooth"
                onClick={() => onSort('activeUsers')}
              >
                <div className="flex items-center gap-2 font-heading font-semibold text-sm text-foreground">
                  <span>Active Users</span>
                  {getSortIcon('activeUsers')}
                </div>
              </th>
              <th className="px-4 py-3 text-left">
                <div className="font-heading font-semibold text-sm text-foreground">
                  Data Isolation
                </div>
              </th>
              <th className="px-4 py-3 text-left">
                <div className="font-heading font-semibold text-sm text-foreground">
                  Contact
                </div>
              </th>
              <th className="px-4 py-3 text-right">
                <div className="font-heading font-semibold text-sm text-foreground">
                  Actions
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {companies?.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <Icon name="Building2" size={48} className="text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">No companies found</p>
                  </div>
                </td>
              </tr>
            ) : (
              companies?.map((company) => (
                <tr key={company?.id} className="hover:bg-muted/30 transition-smooth">
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="font-body font-medium text-sm text-foreground">
                        {company?.businessName}
                      </span>
                      <span className="font-body text-xs text-muted-foreground">
                        {company?.legalName}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${getSubscriptionBadge(company?.subscriptionTier)}`}>
                      {company?.subscriptionTier}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Icon name="Users" size={16} className="text-muted-foreground" />
                      <span className="font-body text-sm text-foreground">{company?.activeUsers || 0}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${getDataIsolationBadge(company?.dataIsolation)}`}>
                      {company?.dataIsolation ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="font-body text-sm text-foreground">
                        {company?.contactPerson}
                      </span>
                      <span className="font-body text-xs text-muted-foreground">
                        {company?.contactEmail}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewDetails(company)}
                        iconName="Eye"
                        aria-label="View details"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onManageSubscription(company)}
                        iconName="CreditCard"
                        aria-label="Manage subscription"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onManageConfiguration(company)}
                        iconName="Settings"
                        aria-label="Configure"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(company)}
                        iconName="Edit"
                        aria-label="Edit"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(company)}
                        iconName="Trash2"
                        aria-label="Delete"
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompanyTable;
